import { store } from '../utils/dependency';
import _ from 'lodash';
import Vue from 'vue';

const BLOCKER_DELAY = 333;
const DEFAULT_CONFIRM_WIDTH = 450;

export const stores = {
  namespaced: true,
  modules: {

    // module for containing errors
    // errors support scoping by formatting key names like "<scope>:<key>". global scope is assumed when only "<key>" is used.
    // some getters below support scoping as optional parameter -- if not specified global scope is assumed.
    // 2022 update - scoping can also be more easily enabled by using startScope and endScope (exposed on the validation object).
    errors: {
      namespaced: true,
      state () {
        return {
          errors: [],
          scope: null
        };
      },
      getters: {
        getAllErrors (state, getters) {
          return (scope) => {
            return getters._getScopedErrors(scope);
          };
        },
        getAllErrorMessages (state, getters) {
          return (scope) => {
            return getters._getScopedErrors(scope).map((e) => {
              return e.message;
            });
          };
        },
        hasErrors (state, getters) {
          return (scope) => {
            return getters._getScopedErrors(scope).length > 0;
          };
        },
        hasFieldErrors (state) {
          // scope is already built into the key name so no additional filtering needed here
          return (key) => {
            let error = state.errors.find((e) => {
              return e.key === key;
            });

            return error !== undefined;
          };
        },
        _getScopedErrors (state) {
          return (scope) => {
            let result = [];
            let prefix = '';
            if (!_.isEmpty(scope))
            {
              prefix = scope + ':';
            }
            else if (!_.isEmpty(state.scope))
            {
              prefix = state.scope + ':';
            }

            if (!_.isEmpty(prefix))
            {
              state.errors.forEach((e) => {
                if (e.key.startsWith(prefix))
                {
                  result.push(e);
                }
              });
            }
            else
            {
              state.errors.forEach((e) => {
                if (!e.key.includes(':'))
                {
                  result.push(e);
                }
              });
            }

            return result;
          };
        }
      },
      mutations: {
        addError (state, { key, message }) {
          let prefix = '';
          if (!_.isEmpty(state.scope))
          {
            prefix = state.scope + ':';
          }

          state.errors.push({ key: `${prefix}${key}`, message });
        },
        removeError (state, key) {
          let prefix = '';
          if (!_.isEmpty(state.scope))
          {
            prefix = state.scope + ':';
          }

          let index = _.findIndex(state.errors, ['key', `${prefix}${key}`]);
          if (index >= 0)
          {
            Vue.delete(state.errors, index);
          }
        },
        clear (state, scope) {
          let prefix = '';
          if (!_.isEmpty(scope))
          {
            prefix = scope + ':';
          }
          else if (!_.isEmpty(state.scope))
          {
            prefix = state.scope + ':';
          }

          if (!_.isEmpty(prefix))
          {
            state.errors = _.filter(state.errors, (e) => {
              return !e.key.startsWith(prefix);
            });
          }
          else
          {
            state.errors = _.filter(state.errors, (e) => {
              return e.key.includes(':');
            });
          }
        },
        startScope (state, scope) {
          state.scope = scope;
        },
        endScope (state) {
          state.scope = null;
        }
      }
    },

    // module for controlling the centralized alert/snackbar notification widget
    snackbar: {
      namespaced: true,
      state () {
        return {
          color: '',
          message: '',
          visible: false
        };
      },
      mutations: {
        show (state, payload) {
          state.color = payload.color;
          state.message = payload.message;
          state.visible = true;
        },
        setVisible (state, value) {
          state.visible = value;
        }
      }
    },

    // module for controlling the screen blocker
    blocker: {
      namespaced: true,
      state () {
        return {
          delay: BLOCKER_DELAY,
          visible: false
        };
      },
      mutations: {
        block (state, { delay = BLOCKER_DELAY } = {}) {
          state.delay = delay;
          state.visible = true;
        },
        unblock (state) {
          state.visible = false;
        }
      }
    },

    // module for controlling the confirmation dialog
    confirm: {
      namespaced: true,
      state () {
        return {
          message: null,
          subtitleKey: null,
          typingPromptKey: null,
          resolve: null,
          width: null,
          visible: false
        };
      },
      mutations: {
        show (state, { message, resolve, subtitleKey = null, typingPromptKey = null, width = DEFAULT_CONFIRM_WIDTH }) {
          state.message = message;
          state.resolve = resolve;
          state.subtitleKey = subtitleKey;
          state.typingPromptKey = typingPromptKey;
          state.width = width;
          state.visible = true;
        },
        resolve (state, value) {
          state.visible = false;
          state.resolve(value);
        }
      },
      actions: {
        // has to be an action as mutations cannot return values
        show ({ commit }, payload) {
          return new Promise((resolve) => {
            commit('show', { ...payload, resolve });
          });
        }
      }
    }
  }
};

export const helpers = {
  block (payload) {
    store.commit('cove/blocker/block', payload);
  },
  unblock () {
    store.commit('cove/blocker/unblock');
  },
  notify (payload) {
    store.commit('cove/snackbar/show', payload);
  },
  confirm (payload) {
    return store.dispatch('cove/confirm/show', payload);
  },
  clearErrors () {
    store.commit('cove/errors/clear');
  }
};